import createClient, { type Middleware } from 'openapi-fetch'

import { GPU_ADMIN_ENDPOINT } from '@/config/runtime'
import { authService } from '@/services/authService'

import { ApiError } from '../unwrapApiResult'
import type { components, paths } from './schema'

// Admin API of gpu-cloud, a separate service (graph @cloudless/fluence, #2972, #3094).
export type GpuSchemas = components['schemas']

export const gpuAdminConfigured = Boolean(GPU_ADMIN_ENDPOINT)

export const gpuClient = createClient<paths>({ baseUrl: GPU_ADMIN_ENDPOINT })

// Bearer only: a 401 from this service is its own refusal and must not end
// the operator's vodopad session the way the main client's middleware does.
const bearer: Middleware = {
  async onRequest({ request }) {
    const { accessToken } = await authService.getTokens()
    request.headers.set('Authorization', `Bearer ${accessToken.value}`)
    return request
  },
}

gpuClient.use(bearer)

type GpuResult<T> = {
  response: Response
  data?: T
  error?: GpuSchemas['ErrorBody'] | unknown
}

function message(error: unknown, response: Response, fallback: string) {
  const body = error as Partial<GpuSchemas['ErrorBody']> | undefined
  return body?.error?.message || response.statusText || fallback
}

export function unwrapGpu<T>(result: GpuResult<T>, fallback: string): T {
  const { response, data, error } = result
  if (!response.ok || data === undefined) {
    throw new ApiError(message(error, response, fallback), response.status)
  }
  return data
}

// For writes answered with 204 No Content.
export function expectGpuOk(result: GpuResult<unknown>, fallback: string) {
  const { response, error } = result
  if (!response.ok) {
    throw new ApiError(message(error, response, fallback), response.status)
  }
}
